import React from "react";
import {
    ShimmeredDetailsList,
    createListTableColumns,
    ensure,
    Checkbox,
    DetailsRowProps,
    RenderFunction,
    Stack,
    Link,
    ResolutionProperty,
    NamedProperty,
    CodedProperty,
    stringifyResolutionProperty,
    Text,
    WarningMessage
} from "sprint-common-web";

import { getCloudAssetDetailsPageHref } from "../../../../assets/AssetsRoutes";
import { ContentSubtype } from "../../../../common/types/ContentSubtype";

export interface VideoSelectionListItem {
    assetId?: string;
    aspectRatio: NamedProperty;
    sourceResolution?: ResolutionProperty;
    targetResolution?: ResolutionProperty;
    subtitle: CodedProperty;
}

type VideoSelectionListProps = {
    videos: VideoSelectionListItem[];
    contentSubtype: ContentSubtype | undefined;
    selectVideo(assetId: string): void;
    selectedVideo?: string;
    targetResolution?: ResolutionProperty;
    isLoading: boolean;
};

export const VideoSelectionList: React.FC<VideoSelectionListProps> = ({
    videos,
    isLoading,
    contentSubtype,
    selectVideo,
    selectedVideo,
    targetResolution
}: VideoSelectionListProps) => {
    const items = videos.map((video) => mapAssetToRelevantVideoItem({ video, targetResolution, contentSubtype }));

    const onRenderAction = (item?: IRelevantVideoItem) => {
        ensure(item);

        return (
            <Checkbox
                checked={item.assetId === selectedVideo}
                onChange={() => selectVideo(item.assetId)}
                styles={{ checkbox: { borderRadius: "50%" } }}
            />
        );
    };

    const columns = createListTableColumns<IRelevantVideoItem>([
        {
            type: "basic",
            key: "assetId",
            name: "Video",
            minWidth: 200,
            maxWidth: 200,
            onRender: onRenderVideoLink
        },
        {
            type: "basic",
            key: "aspectRatio",
            name: "Aspect ratio",
            minWidth: 50,
            maxWidth: 100
        },
        {
            type: "basic",
            key: "sourceResolution",
            name: "Source resolution",
            minWidth: 120,
            maxWidth: 120
        },
        {
            type: "basic",
            key: "targetResolution",
            name: "Target resolution",
            minWidth: 110,
            maxWidth: 110
        },
        {
            type: "basic",
            key: "subtitle",
            name: "Embedded subtitles",
            minWidth: 100,
            maxWidth: 150
        },
        {
            type: "basic",
            key: "assetId",
            name: "Action",
            minWidth: 50,
            maxWidth: 100,
            onRender: onRenderAction
        }
    ]);

    const renderRow = (rowProps?: DetailsRowProps, defaultRender?: RenderFunction<DetailsRowProps>) => {
        ensure(rowProps);
        ensure(defaultRender);

        const testProps = {
            "data-testid": "table-row",
            "data-testkey": rowProps.item.id
        };

        return (
            <Stack styles={{ root: { cursor: "pointer" } }} onClick={() => selectVideo(rowProps.item.assetId)}>
                {defaultRender({ ...testProps, ...rowProps }, defaultRender)}
            </Stack>
        );
    };

    if (!videos.length && !isLoading) {
        return <WarningMessage message="No videos available for selection" />;
    }

    return <ShimmeredDetailsList onRenderRow={renderRow} enableShimmer={isLoading} columns={columns} items={items} />;
};

const onRenderVideoLink = (item?: IRelevantVideoItem, index?: number) => {
    ensure(item);

    return (
        <Link href={item.contentSubtype ? `#${getCloudAssetDetailsPageHref(item.assetId)}` : "#"} target="_blank">
            Video {(index || 0) + 1}
        </Link>
    );
};

const mapAssetToRelevantVideoItem = ({
    video,
    targetResolution,
    contentSubtype
}: {
    video: VideoSelectionListItem;
    targetResolution?: ResolutionProperty;
    contentSubtype: ContentSubtype | undefined;
}) => {
    return {
        assetId: video.assetId || "",
        aspectRatio: video.aspectRatio?.name || "-",
        sourceResolution: video.sourceResolution && stringifyResolutionProperty(video.sourceResolution),
        targetResolution: targetResolution ? stringifyResolutionProperty(targetResolution) : "-",
        subtitle: video.subtitle ? getLanguageDisplayName(video.subtitle) : "-",
        contentSubtype
    };
};

interface IRelevantVideoItem {
    assetId: string;
    aspectRatio?: string;
    sourceResolution: string;
    targetResolution?: string;
    subtitle?: string;
    contentSubtype: ContentSubtype | undefined;
}

export const getLanguageDisplayName = (language: CodedProperty): string => {
    return `${language.name} (${language.code})`;
};
